import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { sectionBadge, scrambleText } from '../../utils/animationUtils';

gsap.registerPlugin(ScrollTrigger);

const experienceData = [
    {
        role: "Fullstack Software Engineer & System Consultant",
        type: "FREELANCE",
        num: "01",
        stubBg: '#FFE600',
        stubText: '#111',
        accentColor: '#FFE600',
        company: "Self-Employed",
        date: "Nov 2025 — Present",
        location: "Nganjuk, East Java",
        descriptions: [
            "Providing professional Fullstack Engineering and IT Consultation for diverse clients, from MSMEs to educational institutions and export-oriented businesses.",
            "End-to-end delivery: from server provisioning (Linux VPS, Nginx, SSL) to production deployment.",
            "Integration of secure authentication (OTP, Role-Based Access) and payment gateways (Midtrans).",
        ],
    },
    {
        role: "Frontend Developer & QA Tester",
        type: "AGENCY",
        num: "02",
        stubBg: '#FF3366',
        stubText: '#FFFFFF',
        accentColor: '#FF3366',
        company: "PT. Eternal Jaya Engineering",
        date: "July 2024 — Okt 2025",
        location: "East Java, Indonesia",
        descriptions: [
            "Built and maintained responsive web applications using Next.js, React.js, and Tailwind CSS with high-fidelity UI/UX implementation.",
            "Integrated RESTful APIs with efficient data fetching strategies and comprehensive error handling.",
            "Performed rigorous Manual Testing and User Acceptance Testing (UAT) to validate features and ensure system reliability before deployment.",
        ],
    },
    {
        role: "Intern Technical Mentor",
        type: "EDUCATOR",
        num: "03",
        stubBg: '#AAFF00',
        stubText: '#111',
        accentColor: '#AAFF00',
        company: "PeTIK II Jombang",
        date: "Mar 2025 — Jul 2025",
        location: "Jombang, East Java",
        descriptions: [
            "Guided 8–10 interns across 4 organizations in solving real-world development challenges.",
            "Provided mentorship in frontend structure, feature logic, Git workflow, and deployment strategies.",
            "Facilitated consultations on UI/UX wireframes, JavaScript libraries, and PHP development.",
        ],
    },
    {
        role: "Web Developer & Junior System Admin",
        type: "SYSADMIN",
        num: "04",
        stubBg: '#00C3FF',
        stubText: '#111',
        accentColor: '#00C3FF',
        company: "Pesantren PeTIK",
        date: "Sep 2023 — May 2024",
        location: "Jombang, East Java",
        descriptions: [
            "Managed production server: deployed Laravel applications on Linux VPS, ensuring 99.9% uptime through Nginx and PHP-FPM configuration.",
            "Implemented SSL certificates (Let's Encrypt), MySQL backups, and basic firewall rules to secure user data.",
            "Developed a Licensing Management System using Laravel, handling backend logic and API integrations.",
        ],
    },
    {
        role: "Full-Stack Developer (Intern)",
        type: "INTERN",
        num: "05",
        stubBg: '#111111',
        stubText: '#FFE600',
        accentColor: '#FFE600',
        company: "STT Stikma International",
        date: "May 2023 — Aug 2023",
        location: "Malang, East Java",
        descriptions: [
            "Built responsive web applications using HTML5, CSS, Bootstrap 5, Laravel 10, and MySQL.",
            "Designed and implemented reusable UI components, increasing team development efficiency by 30%.",
            "Optimized database design, resulting in a 20% improvement in query performance.",
        ],
    },
];

const ExperienceSection = () => {
    const containerRef   = useRef(null);
    const closingLineRef = useRef(null);
    const ticketRefs     = useRef([]);
    const stubRefs       = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.set('.exp-badge',        { opacity: 0, scale: 0 });
            gsap.set('.exp-heading-word', { opacity: 0, y: 60 });
            gsap.set('.exp-ticket',       { opacity: 0, y: 40 });
            gsap.set('.exp-bullet',       { opacity: 0, x: -12 });
            gsap.set(closingLineRef.current, { opacity: 0, y: 20 });

            sectionBadge('.exp-badge');

            gsap.to('.exp-heading-word', {
                opacity: 1, y: 0, stagger: 0.1, duration: 0.9, ease: 'expo.out',
                scrollTrigger: {
                    trigger: '.exp-heading-word', start: 'top 95%',
                    toggleActions: 'play none none reverse', fastScrollEnd: true,
                    onEnter: () => scrambleText('.exp-scramble', 'Worked.'),
                },
            });

            // Stagger tickets into view
            gsap.to('.exp-ticket', {
                opacity: 1, y: 0,
                stagger: 0.13, duration: 0.85, ease: 'expo.out',
                scrollTrigger: { trigger: '.exp-stack', start: 'top 85%', toggleActions: 'play none none reverse', fastScrollEnd: true },
            });

            gsap.to('.exp-bullet', {
                opacity: 1, x: 0,
                stagger: 0.05, duration: 0.45, ease: 'expo.out',
                scrollTrigger: { trigger: '.exp-stack', start: 'top 72%', toggleActions: 'play none none reverse', fastScrollEnd: true },
            });

            // Hover: stub expands, shadow → accent, smooth lift
            ticketRefs.current.forEach((ticket, i) => {
                if (!ticket) return;
                const stub   = stubRefs.current[i];
                const accent = experienceData[i].accentColor;

                ticket.addEventListener('mouseenter', () => {
                    gsap.to(ticket, {
                        y: -6, scale: 1.008,
                        boxShadow: `10px 10px 0 ${accent}`,
                        duration: 0.4, ease: 'power3.out', overwrite: 'auto',
                    });
                    gsap.to(stub, { width: '34%', duration: 0.5, ease: 'power3.out', overwrite: 'auto' });
                });
                ticket.addEventListener('mouseleave', () => {
                    gsap.to(ticket, {
                        y: 0, scale: 1,
                        boxShadow: '5px 5px 0 #111',
                        duration: 0.6, ease: 'power2.inOut', overwrite: 'auto',
                    });
                    gsap.to(stub, { width: '28%', duration: 0.5, ease: 'power2.inOut', overwrite: 'auto' });
                });
            });

            // Parallax scrub via Lenis
            ticketRefs.current.forEach((ticket) => {
                if (!ticket) return;
                gsap.fromTo(ticket,
                    { yPercent: 4 },
                    { yPercent: -2, ease: 'none', scrollTrigger: { trigger: ticket, start: 'top bottom', end: 'bottom top', scrub: 1.4 } }
                );
            });

            gsap.to(closingLineRef.current, {
                opacity: 1, y: 0, duration: 0.7, ease: 'expo.out',
                scrollTrigger: { trigger: closingLineRef.current, start: 'top 88%', toggleActions: 'play none none reverse', fastScrollEnd: true },
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="experience"
            ref={containerRef}
            className="section-dot-bg py-24 font-jakarta bg-[#F2EFE9] overflow-hidden"
        >
            <div className="max-w-5xl mx-auto px-6">

                {/* Header */}
                <div className="mb-14">
                    <div
                        className="exp-badge inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C1E] text-white border-2 border-[#111] rounded-full mb-5"
                        style={{ boxShadow: '3px 3px 0 #111', fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase' }}
                    >
                        <span className="w-2 h-2 rounded-full bg-[#FFE600] inline-block" />
                        EXPERIENCE
                    </div>
                    <h2 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05, fontSize: 'var(--text-h1)', color: 'var(--ink)' }}>
                        <span className="exp-heading-word inline-block mr-3">Where I've</span>
                        <span className="exp-heading-word exp-scramble inline-block" style={{ color: '#FF3366', fontStyle: 'italic' }}>Worked.</span>
                    </h2>
                </div>

                {/* Card stack */}
                <div className="exp-stack" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {experienceData.map((exp, i) => (
                        <div
                            key={i}
                            ref={el => ticketRefs.current[i] = el}
                            className="exp-ticket"
                            style={{
                                display: 'flex',
                                border: '3px solid #111',
                                borderRadius: 12,
                                boxShadow: '5px 5px 0 #111',
                                overflow: 'hidden',
                                cursor: 'default',
                                minHeight: 150,
                            }}
                        >
                            {/* STUB — solid color left block */}
                            <div
                                ref={el => stubRefs.current[i] = el}
                                style={{
                                    width: '28%',
                                    flexShrink: 0,
                                    backgroundColor: exp.stubBg,
                                    borderRight: '3px solid #111',
                                    padding: '22px 20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Ticket Number */}
                                <span style={{
                                    fontFamily: "'Space Mono', monospace", fontWeight: 700,
                                    fontSize: 'clamp(32px, 3.5vw, 48px)',
                                    color: exp.stubText, lineHeight: 1, letterSpacing: '-0.02em',
                                    zIndex: 1,
                                }}>
                                    #{exp.num}
                                </span>

                                {/* Date */}
                                <span style={{
                                    fontFamily: "'Space Mono', monospace", fontSize: 9, fontWeight: 700,
                                    color: exp.stubText === '#111' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.5)',
                                    letterSpacing: '0.06em', textTransform: 'uppercase', zIndex: 1,
                                }}>
                                    {exp.date}
                                </span>
                            </div>

                            {/* CONTENT — white right block */}
                            <div style={{
                                flex: 1,
                                backgroundColor: '#FFFFFF',
                                padding: '22px 26px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                            }}>
                                {/* Company + location */}
                                <p style={{
                                    fontFamily: "'Space Mono', monospace", fontSize: 10, fontWeight: 700,
                                    textTransform: 'uppercase', letterSpacing: '0.1em',
                                    color: '#999', marginBottom: 6,
                                }}>
                                    <span style={{ color: exp.accentColor === '#FFE600' && exp.stubBg !== '#111111' ? '#B8A000' : exp.accentColor }}>
                                        {exp.company}
                                    </span>
                                    <span style={{ color: '#bbb' }}> — {exp.location}</span>
                                </p>

                                {/* Role */}
                                <h3 style={{
                                    fontFamily: "'Syne', sans-serif", fontWeight: 800,
                                    fontSize: 'clamp(15px, 1.6vw, 21px)', color: '#111',
                                    lineHeight: 1.2, letterSpacing: '-0.02em', marginBottom: 12,
                                }}>
                                    {exp.role}
                                </h3>

                                {/* Accent rule */}
                                <div style={{
                                    width: 36, height: 3,
                                    backgroundColor: exp.stubBg === '#111111' ? '#FFE600' : exp.stubBg,
                                    border: '1.5px solid #111', borderRadius: 1, marginBottom: 12,
                                }} />

                                {/* Bullets */}
                                <ul style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                                    {exp.descriptions.map((d, j) => (
                                        <li key={j} className="exp-bullet" style={{
                                            fontFamily: "'DM Sans', sans-serif", fontSize: 13.5,
                                            lineHeight: 1.65, color: '#555', display: 'flex', gap: 10,
                                        }}>
                                            <span style={{
                                                flexShrink: 0, width: 18, height: 18,
                                                backgroundColor: exp.stubBg === '#111111' ? '#FFE600' : exp.stubBg,
                                                border: '2px solid #111', borderRadius: 3,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                marginTop: 2, fontSize: 10, fontWeight: 900,
                                                color: exp.stubText === '#FFFFFF' ? '#fff' : '#111',
                                                fontFamily: "'Space Mono', monospace",
                                            }}>→</span>
                                            {d}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}

                    {/* Closing stat */}
                    <div
                        ref={closingLineRef}
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            gap: 16, marginTop: 20,
                            fontFamily: "'Space Mono', monospace", fontSize: 11,
                            color: '#888', textTransform: 'uppercase', letterSpacing: '0.15em',
                        }}
                    >
                        <span>—</span>
                        <span>2+ years · 4 companies · 6+ projects</span>
                        <span>—</span>
                    </div>
                </div>

            </div>
        </section>
    );
};

export default ExperienceSection;